/**
 * KioskUpsellPopup - Cross-sell/Upsell popup shown after adding item to cart
 * Suggests complementary items based on what was added
 * Includes analytics tracking for conversion optimization
 */
import { useState, useEffect, useRef } from 'react';
import { X, Plus, ShoppingBag, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '../../utils/apiClient';

// Generate or get session ID for tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('kiosk_upsell_session_id');
  if (!sessionId) {
    sessionId = `kiosk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('kiosk_upsell_session_id', sessionId);
  }
  return sessionId;
};

// Track upsell event
const trackUpsellEvent = async (eventType, data) => {
  try {
    await api.post('/upsells/track', {
      event_type: eventType,
      session_id: getSessionId(),
      source: 'kiosk',
      ...data
    });
  } catch (err) {
    console.error('Failed to track upsell event:', err);
  }
};

export default function KioskUpsellPopup({ 
  show, 
  onClose, 
  cartItems, 
  onAddItem,
  isAccessible = false 
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const hasTrackedShown = useRef(false);
  const triggerItemRef = useRef(null);

  useEffect(() => {
    if (show && cartItems.length > 0) {
      hasTrackedShown.current = false;
      // Store the last item as trigger
      triggerItemRef.current = cartItems[cartItems.length - 1];
      fetchSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cartItems]);

  // Track "shown" event when popup displays with suggestions
  useEffect(() => {
    if (show && suggestions.length > 0 && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      const firstSuggestion = suggestions[0];
      trackUpsellEvent('shown', {
        rule_id: triggerItemRef.current?.item_id || null,
        rule_type: firstSuggestion?.source || 'unknown',
        suggested_item_ids: suggestions.map(s => s.id),
        message: message
      });
    }
  }, [show, suggestions, message]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const res = await api.post('/upsells/suggest', {
        cart_items: cartItems.map(item => ({
          item_id: item.item_id,
          name: item.name,
          category: item.category,
          quantity: item.quantity
        })),
        limit: 3
      });
      
      setSuggestions(res.data.suggestions || []);
      setMessage(res.data.message || 'You might also like');
    } catch (err) {
      console.error('Failed to fetch upsell suggestions:', err);
      setSuggestions([]);
    }
    setLoading(false);
  };

  const handleAddSuggestion = (item) => {
    // Track conversion
    trackUpsellEvent('added', {
      rule_id: triggerItemRef.current?.item_id || null,
      rule_type: item.source || 'unknown',
      suggested_item_ids: suggestions.map(s => s.id),
      added_item_id: item.id,
      message: message
    });

    onAddItem({
      item_id: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
      image: item.image,
      category: item.category
    });
    onClose();
  };

  const handleDismiss = () => {
    // Track dismissal
    if (suggestions.length > 0) {
      trackUpsellEvent('dismissed', {
        rule_id: triggerItemRef.current?.item_id || null,
        rule_type: suggestions[0]?.source || 'unknown',
        suggested_item_ids: suggestions.map(s => s.id),
        message: message
      });
    }
    onClose();
  };

  if (!show || suggestions.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-in fade-in duration-200" data-testid="kiosk-upsell-popup">
      <div className={`bg-gray-900 ${isAccessible ? 'rounded-[2rem] p-10 max-w-3xl' : 'rounded-3xl p-8 max-w-2xl'} w-full mx-6`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`${isAccessible ? 'w-14 h-14' : 'w-12 h-12'} rounded-full bg-amber-500/20 flex items-center justify-center`}>
              <Sparkles className={`${isAccessible ? 'w-8 h-8' : 'w-6 h-6'} text-amber-400`} />
            </div>
            <div>
              <h2 className={`${isAccessible ? 'text-3xl' : 'text-2xl'} font-bold text-white`}>
                {message}
              </h2>
              <p className={`${isAccessible ? 'text-lg' : 'text-base'} text-gray-400`}>
                Complete your order with these popular picks
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="text-gray-400 hover:text-white hover:bg-white/10"
            data-testid="kiosk-upsell-close"
          >
            <X className={`${isAccessible ? 'w-8 h-8' : 'w-6 h-6'}`} />
          </Button>
        </div>

        {/* Suggestions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {suggestions.map((item) => (
            <button
              key={item.id}
              onClick={() => handleAddSuggestion(item)}
              className={`bg-gray-800 hover:bg-gray-700 ${isAccessible ? 'rounded-2xl p-6' : 'rounded-xl p-4'} text-left transition-all hover:ring-2 hover:ring-primary/50 active:scale-95`}
              data-testid={`kiosk-upsell-item-${item.id}`}
            >
              {item.image && (
                <img 
                  src={item.image} 
                  alt={item.name}
                  className={`w-full ${isAccessible ? 'h-40' : 'h-32'} object-cover rounded-lg mb-3`}
                />
              )}
              <h3 className={`${isAccessible ? 'text-xl' : 'text-lg'} font-medium text-white mb-1 line-clamp-2`}>
                {item.name}
              </h3>
              <div className="flex items-center justify-between">
                <span className={`${isAccessible ? 'text-2xl' : 'text-xl'} font-bold text-green-400`}>
                  {item.price?.toFixed(2)}
                </span>
                <div className={`${isAccessible ? 'w-12 h-12' : 'w-10 h-10'} rounded-full bg-primary flex items-center justify-center`}>
                  <Plus className={`${isAccessible ? 'w-7 h-7' : 'w-5 h-5'} text-white`} />
                </div>
              </div>
              {item.reason && item.reason !== message && (
                <p className="text-xs text-amber-400 mt-2">{item.reason}</p>
              )}
            </button>
          ))}
        </div>

        {/* Skip Button */}
        <Button
          variant="ghost"
          onClick={handleDismiss}
          className={`w-full ${isAccessible ? 'py-6 text-2xl' : 'py-4 text-xl'} text-gray-400 hover:text-white hover:bg-white/10 rounded-xl`}
          data-testid="kiosk-upsell-skip"
        >
          <ShoppingBag className={`${isAccessible ? 'w-8 h-8' : 'w-6 h-6'} mr-3`} />
          No thanks, continue to cart
        </Button>
      </div>
    </div>
  );
}
